/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                           SCRAPER MONITORING DASHBOARD                                       ║
 * ║                        Clock-It Styled External Scraper Management                          ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Activity, 
  Server, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Building2,
  TrendingUp,
  RefreshCw,
  Play,
  AlertTriangle,
  Cloud,
  Database
} from 'lucide-react';
import { scraperMonitoringService } from '../lib/scraperMonitoringService';
import { handleScraperWebhook, testWebhookSystem, generateTestWebhookData } from '../pages/api/scraper/status';

interface ScraperInfo {
  scraper_id: string;
  name: string;
  deployment_type: 'aws-ec2' | 'aws-lambda' | 'heroku' | 'other';
  status: 'active' | 'idle' | 'error' | 'offline' | 'maintenance';
  last_heartbeat: string;
  last_run?: string;
  last_success?: string;
  error_count: number;
  total_runs: number;
  companies_scraped: number;
  avg_runtime: number;
}

interface DashboardData {
  scrapers: ScraperInfo[];
  summary: {
    total_scrapers: number;
    active_scrapers: number;
    offline_scrapers: number;
    error_scrapers: number;
    total_companies_scraped: number;
    last_updated: string;
  };
  recent_metrics: any[];
}

export function ScraperMonitoringDashboard() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  useEffect(() => {
    loadDashboardData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setRefreshing(true);
      const data = await scraperMonitoringService.getDashboardData();
      if (data) {
        setDashboardData(data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = async () => {
    await loadDashboardData();
  };

  const handleTestWebhook = async () => {
    await testWebhookSystem();
    // Refresh dashboard after test
    setTimeout(loadDashboardData, 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'idle': return 'bg-blue-500';
      case 'error': return 'bg-red-500';
      case 'offline': return 'bg-gray-500';
      case 'maintenance': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'idle': return <Clock className="w-4 h-4" />;
      case 'error': return <AlertCircle className="w-4 h-4" />;
      case 'offline': return <AlertTriangle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getDeploymentIcon = (type: string) => {
    switch (type) {
      case 'aws-ec2': return <Server className="w-4 h-4" />;
      case 'aws-lambda': return <Cloud className="w-4 h-4" />;
      case 'heroku': return <Database className="w-4 h-4" />;
      default: return <Server className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-lg text-gray-600">Loading scraper monitoring...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Scraper Monitoring Dashboard
              </h1>
              <p className="text-gray-600">
                Monitor external data scrapers and their performance metrics
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                onClick={handleTestWebhook}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Play className="w-4 h-4" />
                <span>Test Webhooks</span>
              </Button>
              
              <Button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </Button>
            </div>
          </div>
          
          {lastUpdate && (
            <p className="text-sm text-gray-500 mt-2">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Summary Cards */}
        {dashboardData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Scrapers</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {dashboardData.summary.total_scrapers}
                    </p>
                  </div>
                  <Server className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active Scrapers</p>
                    <p className="text-2xl font-bold text-green-600">
                      {dashboardData.summary.active_scrapers}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Companies Scraped</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {dashboardData.summary.total_companies_scraped.toLocaleString()}
                    </p>
                  </div>
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Error Rate</p>
                    <p className="text-2xl font-bold text-red-600">
                      {dashboardData.summary.error_scrapers}
                    </p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Scraper Status Grid */}
        {dashboardData && dashboardData.scrapers.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {dashboardData.scrapers.map((scraper) => (
              <Card key={scraper.scraper_id} className="border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                      {scraper.name}
                    </CardTitle>
                    <div className="flex items-center space-x-2">
                      {getDeploymentIcon(scraper.deployment_type)}
                      <Badge
                        className={`${getStatusColor(scraper.status)} text-white flex items-center space-x-1`}
                      >
                        {getStatusIcon(scraper.status)}
                        <span className="capitalize">{scraper.status}</span>
                      </Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">{scraper.scraper_id}</p>
                </CardHeader>
                
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Runs</p>
                      <p className="text-lg font-semibold">{scraper.total_runs}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Companies Found</p>
                      <p className="text-lg font-semibold">{scraper.companies_scraped}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Error Count</p>
                      <p className="text-lg font-semibold text-red-600">{scraper.error_count}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Runtime</p>
                      <p className="text-lg font-semibold">
                        {scraper.avg_runtime ? `${scraper.avg_runtime.toFixed(1)}s` : 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {scraper.last_success && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm text-gray-600">
                        Last Success: {new Date(scraper.last_success).toLocaleString()}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <Server className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Scrapers Configured
              </h3>
              <p className="text-gray-600 mb-6">
                Set up external scrapers to monitor their status and performance.
              </p>
              <Button onClick={handleTestWebhook} className="flex items-center space-x-2 mx-auto">
                <Play className="w-4 h-4" />
                <span>Simulate Scraper Data</span>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* System Status */}
        <Alert className="border-blue-200 bg-blue-50">
          <Activity className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Monitoring System Active:</strong> External scrapers can send status updates to{' '}
            <code className="bg-blue-100 px-2 py-1 rounded text-sm">
              {scraperMonitoringService.getWebhookUrl()}
            </code>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}